/**
 * Legacy PascalCase URL → canonical URL redirects.
 *
 * Base44's scaffold used to register every page under both its real route
 * (e.g. "/") AND a PascalCase route named after the component (e.g. "/Home").
 * Those PascalCase routes were later removed, but Google had already indexed
 * a few of them ("soft" 404 — HTTP 200 serving the 404 component), which is
 * why they appear as sitelinks labelled "404".
 *
 * This map catches every known PascalCase variant and 301-style replaces it
 * (window.location.replace — no history entry) with the canonical lowercase
 * route, so users/Google land on the real page instead of the 404 component.
 *
 * Keep this map additive — add new legacy variants as they surface in Search
 * Console without touching existing entries.
 */
export const legacyRedirects = {
  // Home + top-level content pages
  '/Home': '/',
  '/OmOss': '/om-oss',
  '/Process': '/process',
  '/OfferView': '/',
  '/ContractView': '/',
  '/PageNotFound': '/',
  // Collections
  '/SaunaCollectionLY': '/sauna-ly',
  '/SaunaCollectionPanorama': '/sauna-panorama',
  '/SaunaCollectionFjel': '/sauna-fjel',
  // Sauna model pages
  '/SaunaUmi': '/sauna-umi',
  '/SaunaMori': '/sauna-mori',
  '/SaunaHiro': '/sauna-hiro',
  '/SaunaYori': '/sauna-yori',
  '/SaunaTori': '/sauna-tori',
  '/SaunaVerbier': '/sauna-verbier',
  '/SaunaLivigno': '/sauna-hytte',
  '/SaunaHytte': '/sauna-hytte',
  '/SaunaAspen': '/sauna-aspen',
  '/SaunaCortina': '/sauna-cortina',
  '/SaunaDolomiti': '/sauna-dolomiti',
  '/SaunaVikka': '/sauna-vikka',
  '/SaunaChamonix': '/sauna-chamonix',
  '/SaunaTrysil': '/sauna-trysil',
  '/SaunaGeilo': '/sauna-geilo',
  '/SaunaNomad': '/sauna-nomad',
  // Configurators
  '/SaunaUmiKonfigurator': '/sauna-umi-konfigurator',
  '/SaunaHiroKonfigurator': '/sauna-hiro-konfigurator',
  '/SaunaYoriKonfigurator': '/sauna-yori-konfigurator',
  '/SaunaMoriKonfigurator': '/sauna-mori-konfigurator',
  '/SaunaToriKonfigurator': '/sauna-tori-konfigurator',
  '/SaunaVerbierKonfigurator': '/sauna-verbier-konfigurator',
  '/SaunaHytteKonfigurator': '/sauna-hytte-konfigurator',
  '/SaunaTrysilKonfigurator': '/sauna-trysil-konfigurator',
  '/SaunaVikkaKonfigurator': '/sauna-vikka-konfigurator',
  '/SaunaChamonixKonfigurator': '/sauna-chamonix-konfigurator',
  '/SaunaCortinaKonfigurator': '/sauna-cortina-konfigurator',
  '/SaunaDolomitKonfigurator': '/sauna-dolomiti-konfigurator',
  '/SaunaGeiloKonfigurator': '/sauna-geilo-konfigurator',
  '/SaunaAspenKonfigurator': '/sauna-aspen-konfigurator',
  // Tiny BnB
  '/TinyBnb17': '/tiny-bnb-17m',
  // Craftsmanship / education pages
  '/KebonyMaintenance': '/kebony-vedlikehold',
  '/Craftsmanship': '/handverket',
  '/ProcessDetails': '/process-details',
  '/ExteriorPainting': '/eksteriormaling',
  '/SaunaExterior': '/sauna-exterior',
  '/Sertifikater': '/sertifikater',
  '/Portfolio': '/portfolio',
  '/RequestQuote': '/be-om-tilbud',
  '/KebonyKled': '/kebony-kled',
  '/LaerMerHandverk': '/laer-mer-handverk',
  '/OljetTre': '/oljet-tre',
  '/PaintingMaintenance': '/vedlikehold-maling',
  '/PaintProducts': '/produkter-maling',
  '/Arkitektur': '/arkitektur',
  '/Fundament': '/fundament',
  '/RoofingMetal': '/roofing-metal',
  '/RoofingWood': '/roofing-wood',
  '/RoofingGreen': '/roofing-green',
  '/Transport': '/transport',
  // Interior detail pages
  '/InnvendigPanel': '/innvendig-panel',
  '/InnvendigGulv': '/innvendig-gulv',
  '/InnvendigDorer': '/innvendig-dorer',
  '/InnvendigVindu': '/innvendig-vindu',
  '/InnvendigBelysning': '/innvendig-belysning',
  '/InnvendigEkstra': '/innvendig-ekstra',
  // Legal pages
  '/PrivacyPolicy': '/privacy-policy',
  '/CookiePolicy': '/cookie-policy',
  '/TermsOfService': '/terms-of-service',
  // Protected factory/QC pages (already under AdminRoute, but kept for safety)
  '/FactoryPhotoUpload': '/factory-photo-upload',
  '/ClientQCDocumentation': '/client-qc-documentation',
  // Salesman
  '/SalesmanDashboard': '/salesman-dashboard',
  // Admin pages (PascalCase) — redirect to their admin routes
  '/AdminCRM': '/admin-crm',
  '/AdminDashboard': '/admin-dashboard',
  '/AdminManagerDashboard': '/admin-manager-dashboard',
  '/AdminConfigurator': '/admin-configurator',
  '/AdminSaunaPricing': '/admin-sauna-pricing',
  '/AdminMarginDashboard': '/admin-margin-dashboard',
  '/AdminProfitDashboard': '/admin-profit-dashboard',
  '/AdminScheduleCalendar': '/admin-schedule-calendar',
  '/AdminAnalytics': '/admin-analytics',
  '/AdminAnalyticsDashboard': '/admin-analytics-dashboard',
  '/AdminAnalyticsSegmentation': '/admin-analytics-segmentation',
  '/AdminChat': '/admin-chat',
  '/AdminQuoteCreator': '/admin-quote-creator',
  '/AdminOrders': '/admin-orders',
  '/AdminAccounting': '/admin-accounting',
  '/AdminFactoryInvoices': '/admin-factory-invoices',
  '/AdminClientInvoices': '/admin-client-invoices',
  '/AdminProductionPlan': '/admin-production-plan',
  '/AdminFactories': '/admin-factories',
  '/AdminAssemblyCalendar': '/admin-assembly-calendar',
  '/AdminShipments': '/admin-shipments',
  '/AdminQC': '/admin-qc',
  '/AdminProductLifecycle': '/admin-plm',
  '/AdminDocsCatalog': '/admin-docs-catalog',
  '/AdminClaims': '/admin-claims',
  '/AdminCatalog': '/admin-catalog',
  '/AdminOfferCreator': '/admin-offer-creator',
  '/AdminContracts': '/admin-contracts',
  '/AdminInvoices': '/admin-invoices',
  '/AdminSiteAudit': '/admin-site-audit',
  '/AdminSalesAgent': '/admin-sales-agent',
  '/AdminManualSaunaConfigurator': '/admin-manual-sauna-configurator',
};